import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { useHackerContext, useUserContext } from "src/shared/contexts";
import { Field } from "src/shared/contexts/HackerContext/types";

import { PRIMARY_ROLES_ONLY } from "../shift-schedule/constants";
import { useGetMyVolunteerShiftSignupsQuery } from "../shift-schedule/graphql/getMyVolunteerShiftSignups.generated";
import { useGetVolunteerSelfQuery } from "../shift-schedule/graphql/getVolunteerSelf.generated";
import { useGetVolunteerShiftScheduleQuery } from "../shift-schedule/graphql/getVolunteerShiftSchedule.generated";
import RoleLabels from "../volunteers/components/RoleLabels";

export const Profile = () => {
  const { info } = useUserContext();
  const { claimData } = useHackerContext();
  const pronouns = (claimData[Field.PRONOUNS] ?? [])
    .map((pronoun) => pronoun.trim())
    .filter(Boolean)
    .join(", ");
  const { data: getVolunteerSelf, loading: fetchingVolunteerSelf } =
    useGetVolunteerSelfQuery({
      variables: { event_id: EVENT_ID },
    });
  const {
    data: getMyVolunteerShiftSignups,
    loading: fetchingMyVolunteerShiftSignups,
  } = useGetMyVolunteerShiftSignupsQuery({ variables: { event_id: EVENT_ID } });
  const { data: getVolunteerShiftsData, loading: fetchingShifts } =
    useGetVolunteerShiftScheduleQuery({
      variables: { event_id: EVENT_ID },
    });

  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    if (
      getVolunteerSelf &&
      getMyVolunteerShiftSignups &&
      getVolunteerShiftsData
    ) {
      const primary_category_name =
        getVolunteerSelf?.volunteerSelf.primary_category_name;
      const min_primary_signups =
        getVolunteerSelf?.volunteerSelf.min_primary_signups;

      const primaryShiftIds = new Set(
        getVolunteerShiftsData.volunteerShiftSchedule
          .filter((shift) => PRIMARY_ROLES_ONLY.includes(shift.category_name))
          .map((shift) => shift.shift_id)
      );
      const primarySignupCount =
        getMyVolunteerShiftSignups.myVolunteerShiftSignups.filter((signup) =>
          primaryShiftIds.has(signup.shift_id)
        ).length;

      const additionalRoles =
        primarySignupCount >= min_primary_signups
          ? ["Floor Patrol", "Security", "Supplementary"]
          : [];

      setRoles([primary_category_name, ...additionalRoles]);
    }
  }, [
    fetchingVolunteerSelf,
    fetchingMyVolunteerShiftSignups,
    fetchingShifts,
    getMyVolunteerShiftSignups,
    getVolunteerSelf,
    getVolunteerShiftsData,
  ]);

  return (
    <>
      <Header title={info?.name} />

      <PageWrapper fullWidth>
        <InfoContainer>
          <Info>
            <LargeBodyBold>Full Name</LargeBodyBold>
            <Body>{info?.name}</Body>
          </Info>
          <Info>
            <LargeBodyBold>Email</LargeBodyBold>
            <Body>{info?.email}</Body>
          </Info>
          <Info>
            <LargeBodyBold>Pronouns</LargeBodyBold>
            <Body>{pronouns}</Body>
          </Info>
          <Info>
            <LargeBodyBold>Roles</LargeBodyBold>
            <RoleLabels roles={roles} />
          </Info>
        </InfoContainer>
      </PageWrapper>
    </>
  );
};

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
`;

const InfoContainer = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
`;

const LargeBodyBold = styled.p`
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%;
`;

const Body = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 180%;
`;
